/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Button, Icon } from '@mairistem/composants';

import { COLORS } from '@/constants';
import { useOptionsCodeAnts } from '@/hooks';
import type { Mention } from '@/types';
import { AssistantContext } from '@/contexts';

import MentionForm from './MentionForm';
import MentionCard from './MentionCard';

const StepMentions = () => {
  /** Liste des mentions saisies (la plus récente en premier) */
  const { mentions, setMentions } = React.useContext(AssistantContext);
  /** Affichage du formulaire de saisie d'une nouvelle mention (ouvert à l'arrivée) */
  const [isFormOpen, setIsFormOpen] = React.useState<boolean>(true);
  /** Mention en cours de modification (AD-12847) : son formulaire remplace sa carte. */
  const [idEnEdition, setIdEnEdition] = React.useState<string | null>(null);
  /** Options du dropdown « Code ANTS » : les catégories, comme dans l'ETCV. */
  const { options: optionsCodeAnts } = useOptionsCodeAnts(true);

  /*
   * Un seul formulaire ouvert à la fois : ajout et modification se masquent
   * l'un l'autre, pour qu'aucune saisie en cours ne soit perdue sans le savoir.
   */
  const formulaireOuvert = isFormOpen || idEnEdition !== null;

  const handleOpenForm = React.useCallback(() => {
    setIsFormOpen(true);
  }, []);

  const handleCancel = React.useCallback(() => {
    setIsFormOpen(false);
  }, []);

  const handleValidate = React.useCallback(
    (mention: Omit<Mention, 'id'>) => {
      setMentions([{ ...mention, id: _.uniqueId('mention-') }, ...mentions]);
      setIsFormOpen(false);
    },
    [setMentions, mentions],
  );

  const handleEdit = React.useCallback((mention: Mention) => {
    setIdEnEdition(mention.id);
  }, []);

  const handleCancelEdition = React.useCallback(() => {
    setIdEnEdition(null);
  }, []);

  // La mention garde son identifiant et sa place dans la liste.
  const handleValidateEdition = React.useCallback(
    (mention: Omit<Mention, 'id'>) => {
      setMentions(
        _.map(mentions, (existante) =>
          existante.id === idEnEdition
            ? { ...mention, id: existante.id }
            : existante,
        ),
      );
      setIdEnEdition(null);
    },
    [setMentions, mentions, idEnEdition],
  );

  return (
    <div
      className="assistant-step-mentions"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      {!formulaireOuvert && (
        <Button
          basic
          data-testid="mention-ajouter"
          onClick={handleOpenForm}
          style={{
            color: COLORS.Background,
            borderColor: COLORS.Background,
            boxShadow: `0 0 0 1px ${COLORS.Background} inset`,
            flexShrink: 0,
          }}
        >
          <Icon name="plus" />
          Ajouter une mention
        </Button>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75em',
          width: '100%',
          marginTop: '0.75em',
        }}
      >
        {isFormOpen && (
          <MentionForm
            onValidate={handleValidate}
            onCancel={handleCancel}
            numerosExistants={_.map(mentions, (mention) => mention.numero)}
            optionsCodeAnts={optionsCodeAnts}
          />
        )}
        {_.map(mentions, (mention) =>
          mention.id === idEnEdition ? (
            <MentionForm
              key={mention.id}
              mention={mention}
              onValidate={handleValidateEdition}
              onCancel={handleCancelEdition}
              // Son propre numéro n'est pas un doublon.
              numerosExistants={_.map(
                _.reject(mentions, { id: mention.id }),
                (autre) => autre.numero,
              )}
              optionsCodeAnts={optionsCodeAnts}
            />
          ) : (
            <MentionCard
              key={mention.id}
              mention={mention}
              optionsCodeAnts={optionsCodeAnts}
              onEdit={formulaireOuvert ? undefined : handleEdit}
            />
          ),
        )}
      </div>
    </div>
  );
};

export default StepMentions;
